import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  Filter,
  Check,
  UserPlus,
  Heart,
  MessageCircle,
  TrendingUp,
  Flag,
  Clock,
  Settings,
  RefreshCw,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Loader
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import useNotificationsStore from '../../store/notificationsStore';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-hot-toast';

const NotificationsTab = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  const { 
    notifications, 
    unreadCount, 
    pagination,
    isLoading: isStoreLoading,
    getNotifications, 
    getUnreadCount,
    markAsRead, 
    markAllAsRead,
    deleteNotification,
    clearAllNotifications
  } = useNotificationsStore();
  
  const { user } = useAuthStore();

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
  }, [filter]);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      await getNotifications({ 
        type: filter === 'all' ? 'all' : filter,
        page: 1,
        limit: 20 
      });
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      await getUnreadCount();
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications?')) return;

    try {
      await clearAllNotifications();
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'NEW_FOLLOWER': { icon: UserPlus, bg: 'from-blue-500 to-blue-600', color: 'text-blue-600' },
      'POST_LIKE': { icon: Heart, bg: 'from-red-500 to-red-600', color: 'text-red-600' },
      'POST_COMMENT': { icon: MessageCircle, bg: 'from-green-500 to-green-600', color: 'text-green-600' },
      'NEW_MESSAGE': { icon: MessageCircle, bg: 'from-purple-500 to-purple-600', color: 'text-purple-600' },
      'MENTION': { icon: TrendingUp, bg: 'from-orange-500 to-orange-600', color: 'text-orange-600' },
      'SYSTEM': { icon: Bell, bg: 'from-gray-500 to-gray-600', color: 'text-gray-600' },
    };
    const config = icons[type] || icons.SYSTEM;
    const IconComponent = config.icon;
    return { Icon: IconComponent, bg: config.bg };
  };

  const getNotificationAction = (notification) => {
    const data = notification.data || {};
    switch (notification.type) {
      case 'NEW_FOLLOWER':
        return { label: 'View Profile', action: () => navigate(`/community/profile/${data.followerId}`) };
      case 'POST_LIKE':
      case 'MENTION':
        return { label: 'View Post', action: () => navigate(`/community/post/${data.postId}`) };
      case 'POST_COMMENT':
        return { label: 'View Comment', action: () => navigate(`/community/post/${data.postId}`, { state: { commentId: data.commentId, fromCommunity: true } }) };
      case 'NEW_MESSAGE':
        return { label: 'Reply', action: () => navigate(`/community?tab=messages&user=${data.senderId}`) };
      default:
        return null;
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const notifDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.floor((today - notifDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays === 1) {
        return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return formatDistanceToNow(date, { addSuffix: true });
      }
    } catch (error) {
      return '';
    }
  };

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'NEW_FOLLOWER', label: 'Followers' },
    { id: 'POST_LIKE', label: 'Likes' },
    { id: 'POST_COMMENT', label: 'Comments' },
    { id: 'NEW_MESSAGE', label: 'Messages' },
    { id: 'MENTION', label: 'Mentions' },
  ];

  const isLoadingData = isLoading || isStoreLoading;

  return (
    <div className="space-y-3">
      {/* Header - Compact */}
      <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="flex items-center">
              <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              <h2 className="text-base font-semibold text-gray-800">Notifications</h2>
              {unreadCount > 0 && (
                <span className="ml-2 bg-primary-50 text-primary-700 text-[10px] font-medium px-1.5 py-0.5 rounded-md border border-primary-200">
                  {unreadCount} unread
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 ml-3">Stay updated with community activities</p>
          </div>
          
          <div className="flex items-center space-x-1.5">
            <button
              onClick={loadNotifications}
              disabled={isLoadingData}
              className="bg-white rounded-lg px-2.5 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-[11px] font-medium text-gray-700 flex items-center"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingData ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || isLoadingData}
              className="bg-white rounded-lg px-2.5 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-[11px] font-medium text-gray-700 flex items-center disabled:opacity-50"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all
            </button>
            <button
              onClick={handleClearAll}
              disabled={notifications.length === 0 || isLoadingData}
              className="bg-white rounded-lg px-2.5 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-[11px] font-medium text-gray-700 flex items-center disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Filter Tabs - Compact */}
      <div className="bg-white rounded-xl p-2 shadow-neomorph border border-gray-100">
        <div className="flex flex-wrap gap-1.5">
          {filters.map((filterItem) => {
            const count = filterItem.id === 'all' 
              ? notifications.length 
              : notifications.filter(n => n.type === filterItem.id).length;
            
            return (
              <button
                key={filterItem.id}
                onClick={() => setFilter(filterItem.id)}
                className={`
                  px-2.5 py-1 rounded-md text-[11px] font-medium transition-all
                  ${filter === filterItem.id
                    ? 'bg-primary-50 text-primary-700 shadow-neomorph-inset'
                    : 'bg-white text-gray-600 shadow-neomorph hover:shadow-neomorph-hover'
                  }
                `}
              >
                {filterItem.label}
                {count > 0 && (
                  <span className={`ml-1.5 px-1 py-0.5 rounded text-[10px] ${
                    filter === filterItem.id ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications List - Compact */}
      <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100">
        {isLoadingData && notifications.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start space-x-2 p-2 bg-[#E8ECF5] rounded-lg animate-pulse">
                <div className="h-8 w-8 rounded-lg bg-gray-300 shadow-neomorph-inset"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                  <div className="h-2 bg-gray-300 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              const action = getNotificationAction(notification);
              const { Icon, bg } = getNotificationIcon(notification.type);
              
              return (
                <div
                  key={notification.id}
                  className={`flex items-start p-2.5 hover:bg-[#E8ECF5] transition-colors rounded-lg ${
                    !notification.read ? 'bg-blue-50/20' : ''
                  }`}
                >
                  <div className="flex-shrink-0 mr-2.5">
                    <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${bg} shadow-md flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-800">
                          {notification.title}
                        </h4>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                          {notification.message}
                        </p>
                        <div className="flex items-center mt-1.5 text-[10px] text-gray-500">
                          <Clock className="h-2.5 w-2.5 mr-1" />
                          <span className="bg-[#E8ECF5] px-1.5 py-0.5 rounded-md">
                            {formatTime(notification.createdAt)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-0.5 flex-shrink-0">
                        {!notification.read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="p-1 bg-white rounded-md shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-400 hover:text-primary-600"
                            title="Mark as read"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="p-1 bg-white rounded-md shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    
                    {action && (
                      <div className="mt-2">
                        <button
                          onClick={action.action}
                          className="text-[11px] text-primary-600 hover:text-primary-700 bg-primary-50 px-2 py-1 rounded-md inline-flex items-center"
                        >
                          {action.label}
                          <ExternalLink className="h-2.5 w-2.5 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Load More Button */}
            {pagination && pagination.totalPages > pagination.page && (
              <div className="text-center pt-3">
                <button
                  onClick={() => getNotifications({ 
                    type: filter === 'all' ? 'all' : filter,
                    page: pagination.page + 1 
                  })}
                  disabled={isLoadingData}
                  className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 disabled:opacity-50"
                >
                  {isLoadingData ? <Loader className="h-3.5 w-3.5 animate-spin" /> : 'Load More'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Bell className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">No notifications</h3>
            <p className="text-xs text-gray-500">
              {filter === 'all' ? "You're all caught up!" : `No ${filter.toLowerCase()} notifications`}
            </p>
          </div>
        )}
      </div>

      {/* Notification Settings - Compact */}
      <div className="bg-blue-50 rounded-xl p-3 shadow-neomorph-inset border border-blue-200/50">
        <div className="flex items-start">
          <div className="p-1.5 bg-blue-100 rounded-lg shadow-neomorph-inset mr-2.5">
            <Settings className="h-3.5 w-3.5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-xs font-medium text-blue-800">Notification Settings</h4>
            <p className="text-[10px] text-blue-700 mt-0.5">Configure what notifications you want to receive.</p>
            <button 
              onClick={() => window.location.href = '/settings?tab=notifications'}
              className="text-[10px] font-medium text-blue-800 hover:text-blue-900 mt-1.5 bg-blue-100 px-2 py-1 rounded-md inline-flex items-center"
            >
              Go to Settings
              <ExternalLink className="h-2.5 w-2.5 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-lg p-2 shadow-neomorph border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Total</p>
              <p className="text-sm font-bold text-gray-800">{notifications.length}</p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md">
              <Bell className="h-3 w-3 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 shadow-neomorph border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Read</p>
              <p className="text-sm font-bold text-green-600">{notifications.filter(n => n.read).length}</p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
              <CheckCircle className="h-3 w-3 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 shadow-neomorph border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Unread</p>
              <p className="text-sm font-bold text-red-600">{unreadCount}</p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-red-500 to-red-600 rounded-md shadow-md">
              <Bell className="h-3 w-3 text-white" />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 4px 4px 8px #d0d6e0, -4px -4px 8px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 3px 3px 6px #d0d6e0, -3px -3px 6px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2px 2px 4px #d0d6e0, inset -2px -2px 4px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default NotificationsTab;